/* ==========================================================================
   assets/on-media-gallery.js
   Behaviour of snippets/on-product-media-gallery.liquid:
     1. COLOUR FILTER  photos whose alt text is a colour of the product are shown only for that colour
                       (alt text empty / not a colour = shown for every colour)
     2. SLIDER         arrows (desktop), swipe (touch), "1/N" counter that follows the visible photos
     3. VIEWER         click a photo for the full-screen viewer (arrows, swipe, Esc, close)

   What triggers the filter (any of these, so it works with Dawn and with custom pickers):
     - a colour radio / select inside the product section changes   (Dawn's variant picker)
     - Dawn's variant-change message (pubsub)
     - the variant id in the product form changes
     - your own code:  document.dispatchEvent(new CustomEvent('on:variant-change', { detail: { color: 'Black | Apollo' } }))
       (detail can also be { variantId: 1234 })
   No dependencies. Safe to load more than once.
   ========================================================================== */
(function () {
  if (!window.customElements || window.customElements.get('on-product-gallery')) return;

  var norm = function (s) {
    return String(s == null ? '' : s).toLowerCase().replace(/\s+/g, ' ').trim();
  };
  var reduceMotion = function () {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  };

  class OnProductGallery extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this._ready = true;

      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-slide]'));
      this.controls = this.querySelector('[data-controls]');
      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');
      this.curEl = this.querySelector('[data-current]');
      this.totEl = this.querySelector('[data-total]');
      this.lb = this.querySelector('[data-lightbox]');
      this.rtl = getComputedStyle(this).direction === 'rtl';
      this.index = 0;
      this.lbIndex = 0;
      this.colorKey = null;

      this.variantColors = {};
      try {
        var json = this.querySelector('script[data-variants]');
        if (json) this.variantColors = JSON.parse(json.textContent) || {};
      } catch (e) { /* the filter then only follows the colour inputs */ }

      this.abort = new AbortController();
      var sig = { signal: this.abort.signal };

      /* slider */
      if (this.prevBtn) this.prevBtn.addEventListener('click', function () { this.step(-1); }.bind(this), sig);
      if (this.nextBtn) this.nextBtn.addEventListener('click', function () { this.step(1); }.bind(this), sig);
      this.track.addEventListener('scroll', function () {
        if (this._lock || this._raf) return;
        this._raf = window.requestAnimationFrame(function () {
          this._raf = 0;
          this.indexFromScroll();
        }.bind(this));
      }.bind(this), Object.assign({ passive: true }, sig));
      this.track.addEventListener('scrollend', function () { this._lock = false; this.indexFromScroll(); }.bind(this), sig);
      if (window.ResizeObserver) {
        this.ro = new ResizeObserver(function () { this.refresh(); }.bind(this));
        this.ro.observe(this.track);
      }

      /* viewer */
      this.track.addEventListener('click', function (e) {
        var btn = e.target.closest && e.target.closest('[data-open]');
        if (btn) this.openViewer(btn.closest('[data-slide]'));
      }.bind(this), sig);
      this.bindViewer(sig);

      /* colour changes */
      var root = function () { return this.closest('.shopify-section') || document; }.bind(this);
      document.addEventListener('change', function (e) {
        if (!root().contains(e.target)) return;
        this.syncFromPage();
      }.bind(this), sig);
      document.addEventListener('on:variant-change', function (e) {
        var d = (e && e.detail) || {};
        if (d.color != null) this.applyColor(d.color);
        else if (d.variantId != null && this.variantColors[d.variantId] != null) this.applyColor(this.variantColors[d.variantId]);
        else this.syncFromPage();
      }.bind(this), sig);
      window.addEventListener('pageshow', function (e) { if (e.persisted) this.syncFromPage(); }.bind(this), sig);
      this.subscribeToDawn();

      /* first state: the server already filtered for the selected colour, this just takes over */
      this.applyColor(this.dataset.selected, true);
      var fromPage = this.readColor();
      if (fromPage != null && norm(fromPage) !== this.colorKey) this.applyColor(fromPage);
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      if (this.ro) this.ro.disconnect();
      if (this.dawnUnsub) this.dawnUnsub();
      document.documentElement.classList.remove('on-pg-lock');
      this._ready = false;
    }

    /* ---------------------------------------------------------------- colour filter */

    /* Dawn publishes PUB_SUB_EVENTS.variantChange with the chosen variant */
    subscribeToDawn() {
      try {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          var idx = parseInt(this.dataset.colorIndex, 10) || 0;
          var self = this;
          this.dawnUnsub = subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
            var v = event && event.data && event.data.variant;
            if (v && v.options && v.options[idx] != null) self.applyColor(v.options[idx]);
          });
        }
      } catch (e) { /* the change listener above still works */ }
    }

    /* the colour the shopper has chosen, read from the page; null = unknown */
    readColor() {
      var root = this.closest('.shopify-section') || document;
      var name = this.dataset.colorName;
      var i, el;
      if (name) {
        var radios = root.querySelectorAll('input[type="radio"]');
        for (i = 0; i < radios.length; i++) {
          el = radios[i];
          if (el.name === name && el.checked) return el.value;
        }
        var selects = root.querySelectorAll('select');
        for (i = 0; i < selects.length; i++) {
          el = selects[i];
          if (el.name === 'options[' + name + ']' || el.name === name) return el.value;
        }
      }
      var idInput = root.querySelector('form[action*="/cart/add"] [name="id"], input.product-variant-id');
      if (idInput && this.variantColors[idInput.value] != null) return this.variantColors[idInput.value];
      try {
        var v = new URL(window.location.href).searchParams.get('variant');
        if (v && this.variantColors[v] != null) return this.variantColors[v];
      } catch (e) { /* old browser */ }
      return null;
    }

    syncFromPage() {
      var c = this.readColor();
      if (c != null) this.applyColor(c);
    }

    /* show the photos of one colour. force = apply even when the colour did not change */
    applyColor(color, force) {
      var key = norm(color);
      if (!force && key === this.colorKey) return;
      var firstTime = this.colorKey === null;
      this.colorKey = key;

      var any = this.slides.some(function (s) { return !s.dataset.color || s.dataset.color === key; });
      this.slides.forEach(function (s) {
        var show = !any || !s.dataset.color || s.dataset.color === key;
        if (show) s.removeAttribute('hidden');
        else s.setAttribute('hidden', '');
      });

      if (!firstTime) {
        this.index = 0;
        this._lock = false;
        this.track.scrollLeft = 0;
        /* the new set of photos fades in */
        this.track.classList.remove('is-fresh');
        void this.track.offsetWidth;
        this.track.classList.add('is-fresh');
      }
      this.refresh();
    }

    /* ---------------------------------------------------------------- slider */

    visible() {
      return this.slides.filter(function (s) { return !s.hidden; });
    }

    /* how far the slide still is from the left edge of the track (right edge in RTL) */
    delta(slide) {
      var s = slide.getBoundingClientRect();
      var t = this.track.getBoundingClientRect();
      return this.rtl ? s.right - t.right : s.left - t.left;
    }

    indexFromScroll() {
      var vis = this.visible();
      if (!vis.length) return;
      var t = this.track;
      var max = t.scrollWidth - t.clientWidth;
      var pos = Math.abs(t.scrollLeft);
      var best = 0;
      if (max > 2 && pos >= max - 2) {
        best = vis.length - 1;          /* at the very end: the last photo, even when it cannot reach the left edge */
      } else {
        var bestDist = Infinity;
        for (var i = 0; i < vis.length; i++) {
          var d = Math.abs(this.delta(vis[i]));
          if (d < bestDist - 0.5) { bestDist = d; best = i; }
        }
      }
      if (best !== this.index) {
        this.index = best;
        this.refresh();
      }
    }

    goTo(i, smooth) {
      var vis = this.visible();
      if (!vis.length) return;
      i = Math.max(0, Math.min(vis.length - 1, i));
      this.index = i;
      var left = this.delta(vis[i]);
      var behavior = smooth && !reduceMotion() ? 'smooth' : 'auto';
      if (behavior === 'smooth') {
        this._lock = true;
        clearTimeout(this._lockTimer);
        this._lockTimer = setTimeout(function () { this._lock = false; this.indexFromScroll(); }.bind(this), 900);
      }
      if (this.track.scrollBy) this.track.scrollBy({ left: left, behavior: behavior });
      else this.track.scrollLeft += left;
      this.refresh();
    }

    step(dir) {
      this.goTo(this.index + dir, true);
    }

    /* counter, arrows, "has something to scroll" */
    refresh() {
      var n = this.visible().length;
      if (this.index > n - 1) this.index = Math.max(0, n - 1);
      if (this.curEl) this.curEl.textContent = n ? this.index + 1 : 0;
      if (this.totEl) this.totEl.textContent = n;
      if (this.controls) this.controls.hidden = n < 2;
      if (this.prevBtn) this.prevBtn.disabled = this.index <= 0;
      if (this.nextBtn) this.nextBtn.disabled = this.index >= n - 1;
      this.classList.toggle('has-overflow', this.track.scrollWidth > this.track.clientWidth + 1);
    }

    /* ---------------------------------------------------------------- full-screen viewer */

    bindViewer(sig) {
      var lb = this.lb;
      if (!lb) return;
      var self = this;
      this.zoomImg = lb.querySelector('[data-zoom-img]');
      var prev = lb.querySelector('[data-lb-prev]');
      var next = lb.querySelector('[data-lb-next]');
      var close = lb.querySelector('[data-close]');
      var stage = lb.querySelector('[data-stage]');
      var dir = function () { return self.rtl ? -1 : 1; };

      if (prev) prev.addEventListener('click', function () { self.viewerStep(-1); }, sig);
      if (next) next.addEventListener('click', function () { self.viewerStep(1); }, sig);
      if (close) close.addEventListener('click', function () { self.closeViewer(); }, sig);
      if (stage) stage.addEventListener('click', function (e) {
        if (e.target === stage) self.closeViewer();      /* empty space around the photo */
      }, sig);
      lb.addEventListener('click', function (e) { if (e.target === lb) self.closeViewer(); }, sig);
      lb.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); self.viewerStep(dir()); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); self.viewerStep(-dir()); }
      }, sig);
      /* browsers close a <dialog> with Esc themselves */
      lb.addEventListener('close', function () { self.afterViewerClosed(); }, sig);
      lb.addEventListener('cancel', function () { /* Esc: the close event follows */ }, sig);

      /* swipe */
      var x0 = null;
      lb.addEventListener('touchstart', function (e) { x0 = e.changedTouches[0].clientX; }, Object.assign({ passive: true }, sig));
      lb.addEventListener('touchend', function (e) {
        if (x0 === null) return;
        var dx = e.changedTouches[0].clientX - x0;
        x0 = null;
        if (Math.abs(dx) > 50) self.viewerStep(dx < 0 ? dir() : -dir());
      }, sig);
    }

    zoomable() {
      return this.visible().filter(function (s) { return s.dataset.zoom; });
    }

    openViewer(slide) {
      if (!this.lb || !slide) return;
      var list = this.zoomable();
      var i = list.indexOf(slide);
      if (i < 0) return;
      this.lbIndex = i;
      this.renderViewer();
      if (typeof this.lb.showModal === 'function') {
        if (!this.lb.open) this.lb.showModal();
      } else {
        this.lb.setAttribute('open', '');
      }
      document.documentElement.classList.add('on-pg-lock');
    }

    renderViewer() {
      var list = this.zoomable();
      var s = list[this.lbIndex];
      if (!s || !this.zoomImg) return;
      var thumb = s.querySelector('img');
      this.zoomImg.src = s.dataset.zoom;
      this.zoomImg.alt = thumb ? thumb.alt : '';
      var multi = list.length > 1;
      var arrows = this.lb.querySelector('[data-lb-arrows]');
      if (arrows) arrows.hidden = !multi;
      var prev = this.lb.querySelector('[data-lb-prev]');
      var next = this.lb.querySelector('[data-lb-next]');
      if (prev) prev.disabled = this.lbIndex <= 0;
      if (next) next.disabled = this.lbIndex >= list.length - 1;
      if (prev) prev.style.opacity = prev.disabled ? '0.3' : '';
      if (next) next.style.opacity = next.disabled ? '0.3' : '';
    }

    viewerStep(dir) {
      var list = this.zoomable();
      var i = Math.max(0, Math.min(list.length - 1, this.lbIndex + dir));
      if (i === this.lbIndex) return;
      this.lbIndex = i;
      this.renderViewer();
    }

    closeViewer() {
      if (!this.lb) return;
      if (typeof this.lb.close === 'function' && this.lb.open) this.lb.close();
      else {
        this.lb.removeAttribute('open');
        this.afterViewerClosed();
      }
    }

    afterViewerClosed() {
      document.documentElement.classList.remove('on-pg-lock');
      /* the slider continues where the viewer stopped */
      var list = this.zoomable();
      var s = list[this.lbIndex];
      if (s) {
        var at = this.visible().indexOf(s);
        if (at > -1) this.goTo(at, false);
      }
    }
  }

  window.customElements.define('on-product-gallery', OnProductGallery);
})();